import { ScrollArea } from '@heidi/design-system/scroll-area';
import { cn } from '@heidi/utils';
import {
  Ban,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Lightbulb,
  Monitor,
  PenLine,
  Sparkles,
  X,
} from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { type ComputerUseStatus } from '../../../contracts/projects/computer-use-status';
import type { DelegateTraceEntry } from '../../../data/projects/lib/computer-use-trace.types';
import { sanitizeComputerUseTraceDetail } from '../../../data/projects/lib/sanitizeComputerUseTraceDetail';

export const TerminalRecap = ({
  status,
  trace,
  showFullTrace,
  onToggleFullTrace,
}: {
  status: ComputerUseStatus;
  trace: DelegateTraceEntry[];
  showFullTrace: boolean;
  onToggleFullTrace: () => void;
}) => {
  const stepCount = countTraceSteps(trace);
  const failedActionCount = consecutiveFailedActionCount(trace);
  const finalEntry =
    [...trace].reverse().find((e) => e.kind === 'task_complete') ?? trace.at(-1);
  const finalDetail =
    status === 'error' ? undefined : sanitizeComputerUseTraceDetail(finalEntry?.detail);

  return (
    <div className="flex flex-col gap-2 border-t border-border/60 pt-3">
      <div className="flex min-w-0 items-center gap-2">
        {status === 'success' ? (
          <Check className="size-3.5 shrink-0 text-validation-success" />
        ) : status === 'cancelled' ? (
          <Ban className="size-3.5 shrink-0 text-text-tertiary" />
        ) : (
          <X className="size-3.5 shrink-0 text-validation-destructive" />
        )}
        <span className="min-w-0 flex-1 truncate text-xs text-text-secondary">
          {status === 'success' ? (
            `Completed in ${stepCount} step${stepCount === 1 ? '' : 's'}`
          ) : status === 'cancelled' ? (
            `Cancelled after ${stepCount} step${stepCount === 1 ? '' : 's'}`
          ) : failedActionCount > 0 ? (
            <FormattedMessage
              defaultMessage="Stopped after {count, plural, one {# failed action} other {# failed actions}}"
              id="5rtwQ03MQG"
              values={{ count: failedActionCount }}
            />
          ) : (
            `Stopped after ${stepCount} step${stepCount === 1 ? '' : 's'}`
          )}
          {finalDetail && (
            <span className="ml-1 text-text-tertiary">— {finalDetail}</span>
          )}
        </span>
      </div>
      <TraceToggleButton
        expanded={showFullTrace}
        count={stepCount}
        expandedLabel="Hide steps"
        collapsedLabel="Show steps"
        onClick={onToggleFullTrace}
      />
      {showFullTrace && <FullTracePanel trace={trace} />}
    </div>
  );
};

const TraceToggleButton = ({
  expanded,
  count,
  expandedLabel,
  collapsedLabel,
  onClick,
}: {
  expanded: boolean;
  count: number;
  expandedLabel: string;
  collapsedLabel: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex w-fit items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-text-secondary',
      'hover:bg-background-secondary'
    )}
    aria-expanded={expanded}
    aria-controls="computer-use-trace"
  >
    {expanded ? (
      <ChevronDown className="size-3" />
    ) : (
      <ChevronRight className="size-3 rtl:-scale-x-100" />
    )}
    {expanded ? expandedLabel : collapsedLabel}
    <span className="ml-1 rounded bg-background-tertiary px-1 text-[10px] text-text-tertiary">
      {count}
    </span>
  </button>
);

/** The mapper keeps full planner blocks; the expanded trace shows a short line. */
const MAX_PLANNER_LABEL_CHARS = 300;

const capLabel = (s: string, max: number): string =>
  s.length <= max ? s : `${s.slice(0, max - 1)}\u2026`;

const FullTracePanel = ({ trace }: { trace: DelegateTraceEntry[] }) => (
  <ScrollArea
    id="computer-use-trace"
    type="always"
    scrollbarVariant="minimal"
    viewportClassName="max-h-72"
    className={cn(
      'rounded-lg border border-border bg-background-secondary/40 p-2',
      'duration-200 animate-in fade-in'
    )}
  >
    <ol className="flex flex-col gap-1">
      {[...trace].reverse().map((entry) => (
        <li
          key={entry.id}
          className="flex items-start gap-2.5 rounded-md px-2 py-1.5"
          data-trace-status={entry.isError ? 'error' : undefined}
        >
          <TraceEntryIcon entry={entry} />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span
              className={cn(
                'min-w-0 break-words text-xs leading-snug',
                entry.kind === 'planner_text'
                  ? 'text-text-secondary'
                  : entry.kind === 'lifecycle'
                    ? 'text-text-tertiary'
                    : 'text-text-primary'
              )}
            >
              {entry.kind === 'planner_text'
                ? capLabel(entry.label, MAX_PLANNER_LABEL_CHARS)
                : entry.label}
            </span>
            {entry.detail && (
              <span className="break-words text-[11px] leading-snug text-text-tertiary">
                {entry.detail}
              </span>
            )}
          </div>
          <span className="shrink-0 pt-0.5 text-[10px] tabular-nums text-text-tertiary">
            {formatTraceTime(entry.at)}
          </span>
        </li>
      ))}
    </ol>
  </ScrollArea>
);

const TraceEntryIcon = ({ entry }: { entry: DelegateTraceEntry }) => {
  if (entry.isError) {
    return (
      <span className="flex size-5 shrink-0 items-center justify-center rounded bg-validation-destructive-muted">
        <X className="size-3 text-validation-destructive" />
      </span>
    );
  }

  switch (entry.kind) {
    case 'planner_text':
      return (
        <span className="flex size-5 shrink-0 items-center justify-center rounded bg-sunlight-50">
          <Lightbulb className="size-3 text-sunlight-500" />
        </span>
      );
    case 'executor_text':
      return (
        <span className="flex size-5 shrink-0 items-center justify-center rounded bg-sunlight-50">
          <Sparkles className="size-3 text-sunlight-500" />
        </span>
      );
    case 'tool_call':
      return (
        <span className="flex size-5 shrink-0 items-center justify-center rounded bg-background-tertiary">
          <Monitor className="size-3 text-text-primary" />
        </span>
      );
    case 'step_progress':
      return (
        <span className="flex size-5 shrink-0 items-center justify-center rounded bg-background-tertiary">
          <PenLine className="size-3 text-text-secondary" />
        </span>
      );
    case 'tool_result':
    case 'task_complete':
      return (
        <span className="flex size-5 shrink-0 items-center justify-center rounded bg-validation-success-muted">
          <CheckCircle2 className="size-3 text-validation-success" />
        </span>
      );
    case 'lifecycle':
      return (
        <span className="flex size-5 shrink-0 items-center justify-center rounded bg-background-tertiary">
          <CircleDot className="size-3 text-text-tertiary" />
        </span>
      );
  }
};
const countTraceSteps = (trace: DelegateTraceEntry[]): number =>
  trace.filter(
    (e) =>
      e.kind === 'planner_text' || e.kind === 'tool_call' || e.kind === 'step_progress'
  ).length;
const consecutiveFailedActionCount = (trace: DelegateTraceEntry[]): number => {
  let count = 0;
  for (const entry of trace) {
    if (entry.kind !== 'tool_result') continue;
    count = entry.isError ? count + 1 : 0;
  }
  return count;
};
const formatTraceTime = (ms: number): string => {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};
const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
